import React, { useState, useEffect } from 'react';
import { Monitor, Sparkles, Folder, FileText, Music, Image, CheckCircle, X } from 'lucide-react';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import { Button } from './ui/button';

interface ResetSuccessPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

export const ResetSuccessPopup: React.FC<ResetSuccessPopupProps> = ({ isVisible, onClose }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Delay content appearance for smooth animation
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const features = [
    {
      icon: <Folder className="w-6 h-6 text-primary" />,
      title: "Fresh File System",
      description: "Clean MapleOS directory structure"
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-500" />,
      title: "Sample Documents", 
      description: "Ready-to-use text files and folders"
    },
    {
      icon: <Music className="w-6 h-6 text-purple-500" />,
      title: "Audio Collection",
      description: "Ambient tracks and sound files"
    },
    {
      icon: <Image className="w-6 h-6 text-green-500" />,
      title: "Image Gallery",
      description: "Beautiful vacation photos"
    }
  ];

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div 
        className={`relative bg-gradient-to-br from-card to-card/90 border border-border rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transition-all duration-500 ${
          showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Header with close button */}
        <div className="absolute top-4 right-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 hover:bg-white/10 text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </Button>
        </div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full animate-pulse" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-secondary/5 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>

        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Success icon */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle size={40} className="text-white" />
              </div>
              <div className="absolute -top-2 -right-2 animate-spin" style={{ animationDuration: '3s' }}>
                <Sparkles size={24} className="text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Main message */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2 animate-fade-in">
              Welcome to MapleOS!
            </h1>
            <p className="text-muted-foreground animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Your system has been successfully reset and is ready for a fresh start.
            </p>
          </div>

          {/* Features grid */}
          <div className="mb-6 space-y-3">
            <h3 className="text-lg font-semibold text-foreground mb-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              What's New:
            </h3>
            
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-all duration-300 animate-slide-in-right"
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {feature.icon}
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-foreground text-sm">{feature.title}</h4>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* MapleOS branding */}
          <div className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg animate-fade-in" style={{ animationDelay: '1s' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapleLeaf size={20} className="text-primary" />
              <span className="font-bold text-foreground">MapleOS</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Control your intelligence. Own your horizon.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 animate-fade-in" style={{ animationDelay: '1.2s' }}>
            <Button
              onClick={onClose}
              className="flex-1 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-medium"
            >
              Start Exploring
            </Button>
          </div>
        </div>

        {/* Floating sparkles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${15 + i * 10}%`,
                top: `${20 + (i % 3) * 20}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: '3s'
              }}
            >
              <Sparkles size={12} className="text-yellow-400/60" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};